import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Stack,
  Alert,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import RefreshButton from '../components/RefreshButton';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloudIcon from '@mui/icons-material/Cloud';
import BackupIcon from '@mui/icons-material/Backup';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { storagePoolService, type StoragePool, type DestinationInfo } from '@/services/storage-pool.service';
import type { BackupDestination } from '@/types/backup-destination.types';
import { STORAGE_POOL_TYPES, type StoragePoolType } from '@/types/enums.generated';
import { useAuthStore } from '@/stores/authStore';
import { fieldErrorsFromApi, useFieldErrors } from '@/utils/formErrors';

// ADR-050: pools are managed here. admin creates / edits / enables / deletes
// pools and adds / removes their members; sub_admin sees the same page
// without the write controls (the backend answers 403 either way).
//
// Cloud destinations (AWS / GCP / Azure) are the worker's deployment
// configuration, not database rows (ADR-041), so they cannot be pool members
// in pool_destinations. Product decision 2026-09-29: every 智慧分散池 shows
// them as its built-in members (not addable, not removable) and on-premise
// destinations can be added on top; a 備份池 has no built-in members and
// says the clouds cannot join it. Whether the worker places shards this way
// is the worker-side ADR's business — this page describes the configuration.

const POOL_TYPE_LABELS: Record<StoragePoolType, string> = {
  smart_distribution: '智慧分散池',
  backup: '備份池',
};

const POOL_TYPE_HINTS: Record<StoragePoolType, string> = {
  smart_distribution: '預設納入部署設定的所有雲端目的地，12 片碎片平均分散；可再加入地端目的地',
  backup: '只能加入地端目的地，池內每個儲存空間都保存完整的 12 片碎片；雲端目的地無法加入備份池',
};

const POOL_TYPE_COLORS: Record<StoragePoolType, 'primary' | 'secondary'> = {
  smart_distribution: 'primary',
  backup: 'secondary',
};

const POOL_TYPE_ICONS: Record<StoragePoolType, React.ReactNode> = {
  smart_distribution: <CloudIcon fontSize="small" />,
  backup: <BackupIcon fontSize="small" />,
};

const STATUS_COLORS: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  healthy: 'success',
  unhealthy: 'error',
  verifying: 'warning',
  not_configured: 'default',
};

const STATUS_LABELS: Record<string, string> = {
  healthy: '已連線',
  unhealthy: '連線異常',
  verifying: '驗證中',
  not_configured: '未設定',
};

const NAME_REQUIRED = '請輸入儲存池名稱';
const NAME_MAX = 100;
const NAME_TOO_LONG = `儲存池名稱最多 ${NAME_MAX} 個字元`;
const MEMBER_REQUIRED = '請選擇要加入的儲存空間';
const PRIORITY_INVALID = '優先級請輸入 0 或以上的整數';

const SMART_POOL_NOTE = '智慧分散池預設納入部署設定的所有雲端目的地，不需也不能手動加入或移除；地端目的地可另外加入。';
const SMART_POOL_NO_CLOUD = '部署尚未設定任何雲端目的地，這個智慧分散池目前沒有預設成員；地端目的地仍可加入。';
// ADR-052 D2: how much of the data one backup member could restore if
// every other storage space were lost.
const COVERAGE_HINT = '若其他儲存空間全部失效，只靠這個儲存空間能還原的檔案數。新加入的儲存空間或備份池建立前的舊檔案，系統會在背景自動補齊。';

function coverageCell(dest: DestinationInfo) {
  const c = dest.coverage;
  if (!c) return '-';
  const complete = c.covered_files >= c.total_files;
  return (
    <Chip
      size="small"
      color={complete ? 'success' : 'warning'}
      variant="outlined"
      label={`已涵蓋 ${c.covered_files.toLocaleString()}／${c.total_files.toLocaleString()} 個檔案`}
      data-testid={`pool-member-coverage-${dest.id}`}
    />
  );
}

const BACKUP_POOL_NOTE = '備份池只能加入地端目的地。雲端目的地（AWS／GCP／Azure）由部署設定，只作為智慧分散池的預設成員，無法加入備份池。';
const PICKER_HELPER: Record<StoragePoolType, string> = {
  smart_distribution: '只列出尚未在此池內的地端目的地；雲端目的地已預設納入智慧分散池，不需加入',
  backup: '只列出尚未在此池內的地端目的地；雲端目的地無法加入備份池',
};

// 409 POOL_NAME_TAKEN lands on the name input; the backend message is Chinese.
const POOL_FORM_FIELDS = { POOL_NAME_TAKEN: 'name' };
// 409 POOL_DESTINATION_EXISTS lands on the destination picker.
const MEMBER_FORM_FIELDS = { POOL_DESTINATION_EXISTS: 'destination_id' };

interface PoolForm {
  name: string;
  type: StoragePoolType;
  description: string;
}

const EMPTY_POOL_FORM: PoolForm = { name: '', type: 'smart_distribution', description: '' };

function typeLabel(type: string): string {
  return POOL_TYPE_LABELS[type as StoragePoolType] || type;
}

function statusChip(status?: string) {
  return (
    <Chip
      label={STATUS_LABELS[status ?? ''] || status || '未知'}
      color={STATUS_COLORS[status ?? ''] || 'default'}
      size="small"
    />
  );
}

export default function StoragePools() {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');

  const [pools, setPools] = useState<StoragePool[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // The worker's cloud destinations: built-in members of every 智慧分散池.
  const [clouds, setClouds] = useState<BackupDestination[]>([]);
  const [cloudsError, setCloudsError] = useState(false);

  // Row menu (admin)
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuPool, setMenuPool] = useState<StoragePool | null>(null);

  // Create / edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingPool, setEditingPool] = useState<StoragePool | null>(null);
  const [form, setForm] = useState<PoolForm>(EMPTY_POOL_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const formFields = useFieldErrors();

  // Delete confirmation
  const [deletingPool, setDeletingPool] = useState<StoragePool | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Detail dialog: keep the id and read the pool from the list so member
  // changes show without closing the dialog.
  const [detailPoolId, setDetailPoolId] = useState<string | null>(null);
  const detailPool = useMemo(() => pools.find((p) => p.id === detailPoolId) ?? null, [pools, detailPoolId]);

  // Add member dialog (admin)
  const [memberOpen, setMemberOpen] = useState(false);
  const [candidates, setCandidates] = useState<BackupDestination[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [memberForm, setMemberForm] = useState({ destination_id: '', priority: '0' });
  const [memberError, setMemberError] = useState<string | null>(null);
  const [memberLoading, setMemberLoading] = useState(false);
  const memberFields = useFieldErrors();

  // Remove member confirmation
  const [removingMember, setRemovingMember] = useState<DestinationInfo | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  const loadPools = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await storagePoolService.listPools();
      setPools(data);
    } catch (err) {
      console.error('Failed to load pools:', err);
      setError('載入儲存池失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadClouds = useCallback(async () => {
    try {
      setCloudsError(false);
      setClouds(await storagePoolService.listCloudDestinations());
    } catch (err) {
      console.error('Failed to load cloud destinations:', err);
      setClouds([]);
      setCloudsError(true);
    }
  }, []);

  useEffect(() => {
    loadPools();
    loadClouds();
  }, [loadPools, loadClouds]);

  const refresh = () => {
    loadPools();
    loadClouds();
  };

  // --- Row menu ---
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, pool: StoragePool) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuPool(pool);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);

  // --- Create / edit ---
  const openCreate = () => {
    setEditingPool(null);
    setForm(EMPTY_POOL_FORM);
    setFormError(null);
    formFields.clear();
    setFormOpen(true);
  };

  const openEdit = (pool: StoragePool) => {
    handleMenuClose();
    setEditingPool(pool);
    setForm({ name: pool.name, type: pool.type, description: pool.description || '' });
    setFormError(null);
    formFields.clear();
    setFormOpen(true);
  };

  const closeForm = () => {
    if (formLoading) return;
    setFormOpen(false);
    setEditingPool(null);
    setFormError(null);
    formFields.clear();
  };

  const submitForm = async () => {
    const name = form.name.trim();
    if (!name) {
      formFields.setField('name', NAME_REQUIRED);
      return;
    }
    if (name.length > NAME_MAX) {
      formFields.setField('name', NAME_TOO_LONG);
      return;
    }
    try {
      setFormLoading(true);
      setFormError(null);
      formFields.clear();
      if (editingPool) {
        await storagePoolService.updatePool(editingPool.id, { name, description: form.description.trim() });
        setSuccessMessage(`已更新儲存池「${name}」`);
      } else {
        await storagePoolService.createPool({ name, type: form.type, description: form.description.trim() || undefined });
        setSuccessMessage(`已建立儲存池「${name}」`);
      }
      setFormOpen(false);
      setEditingPool(null);
      loadPools();
    } catch (err) {
      console.error('Failed to save pool:', err);
      setFormError(formFields.apply(err, POOL_FORM_FIELDS, editingPool ? '更新儲存池失敗，請稍後再試' : '建立儲存池失敗，請稍後再試') || null);
    } finally {
      setFormLoading(false);
    }
  };

  // --- Enable / disable ---
  const toggleEnabled = async (pool: StoragePool) => {
    handleMenuClose();
    try {
      await storagePoolService.updatePool(pool.id, { is_enabled: !pool.is_enabled });
      setSuccessMessage(pool.is_enabled ? `已停用儲存池「${pool.name}」` : `已啟用儲存池「${pool.name}」`);
      loadPools();
    } catch (err) {
      console.error('Failed to toggle pool:', err);
      // 409 POOL_TYPE_ACTIVE / POOL_MEMBERS_INSUFFICIENT carry a Chinese reason; show it.
      setActionError(fieldErrorsFromApi(err, {}, pool.is_enabled ? '停用儲存池失敗' : '啟用儲存池失敗').general);
    }
  };

  // --- Delete ---
  const openDelete = (pool: StoragePool) => {
    handleMenuClose();
    setDeletingPool(pool);
  };

  const confirmDelete = async () => {
    if (!deletingPool) return;
    try {
      setDeleteLoading(true);
      await storagePoolService.deletePool(deletingPool.id);
      setSuccessMessage(`已刪除儲存池「${deletingPool.name}」`);
      if (detailPoolId === deletingPool.id) setDetailPoolId(null);
      setDeletingPool(null);
      loadPools();
    } catch (err) {
      console.error('Failed to delete pool:', err);
      setActionError('刪除儲存池失敗');
    } finally {
      setDeleteLoading(false);
    }
  };

  // --- Members ---
  const openAddMember = async () => {
    setMemberForm({ destination_id: '', priority: '0' });
    setMemberError(null);
    memberFields.clear();
    setMemberOpen(true);
    try {
      setCandidatesLoading(true);
      setCandidates(await storagePoolService.listCandidateDestinations());
    } catch (err) {
      console.error('Failed to load destinations:', err);
      setMemberError('載入儲存空間清單失敗，請稍後再試');
    } finally {
      setCandidatesLoading(false);
    }
  };

  const closeAddMember = () => {
    if (memberLoading) return;
    setMemberOpen(false);
    setMemberError(null);
    memberFields.clear();
  };

  const availableCandidates = useMemo(() => {
    const members = new Set((detailPool?.destinations ?? []).map((d) => d.id));
    return candidates.filter((d) => !members.has(d.id));
  }, [candidates, detailPool]);

  const submitAddMember = async () => {
    if (!detailPool) return;
    if (!memberForm.destination_id) {
      memberFields.setField('destination_id', MEMBER_REQUIRED);
      return;
    }
    const priority = Number(memberForm.priority);
    if (!Number.isInteger(priority) || priority < 0) {
      memberFields.setField('priority', PRIORITY_INVALID);
      return;
    }
    try {
      setMemberLoading(true);
      setMemberError(null);
      memberFields.clear();
      await storagePoolService.addDestination(detailPool.id, memberForm.destination_id, priority);
      const picked = candidates.find((d) => d.id === memberForm.destination_id);
      setSuccessMessage(`已將「${picked?.display_name ?? '儲存空間'}」加入「${detailPool.name}」`);
      setMemberOpen(false);
      loadPools();
    } catch (err) {
      console.error('Failed to add member:', err);
      setMemberError(memberFields.apply(err, MEMBER_FORM_FIELDS, '加入儲存空間失敗，請稍後再試') || null);
    } finally {
      setMemberLoading(false);
    }
  };

  const confirmRemoveMember = async () => {
    if (!detailPool || !removingMember) return;
    try {
      setRemoveLoading(true);
      await storagePoolService.removeDestination(detailPool.id, removingMember.id);
      setSuccessMessage(`已將「${removingMember.name || '儲存空間'}」移出「${detailPool.name}」`);
      setRemovingMember(null);
      loadPools();
    } catch (err) {
      console.error('Failed to remove member:', err);
      // 409 POOL_MEMBERS_INSUFFICIENT: an enabled smart pool may not shrink below 3.
      setActionError(fieldErrorsFromApi(err, {}, '移除儲存空間失敗').general);
    } finally {
      setRemoveLoading(false);
    }
  };

  const columns = useMemo<GridColDef<StoragePool>[]>(
    () => [
      {
        field: 'type',
        headerName: '池類型',
        width: 150,
        renderCell: (params) => (
          <Tooltip title={POOL_TYPE_HINTS[params.value as StoragePoolType] ?? ''}>
            <Chip
              icon={POOL_TYPE_ICONS[params.value as StoragePoolType] as React.ReactElement}
              label={typeLabel(params.value)}
              color={POOL_TYPE_COLORS[params.value as StoragePoolType] || 'default'}
              size="small"
            />
          </Tooltip>
        ),
      },
      {
        field: 'name',
        headerName: '名稱',
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2" fontWeight={500}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'description',
        headerName: '說明',
        flex: 1.5,
        renderCell: (params) => (
          <Typography variant="body2" color="text.secondary">
            {params.value || '-'}
          </Typography>
        ),
      },
      {
        // Sorts by the on-premise member count; the cloud count is the same
        // for every 智慧分散池 (the deployment's clouds) and 0 for a 備份池.
        field: 'destinations',
        headerName: '儲存空間',
        width: 190,
        align: 'center',
        headerAlign: 'center',
        valueGetter: (_value: unknown, row: StoragePool) => row.destinations?.length || 0,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            {params.row.type === 'smart_distribution' && (
              <Tooltip title={SMART_POOL_NOTE}>
                <Chip label={`雲端 ${clouds.length}`} size="small" color="primary" variant="outlined" />
              </Tooltip>
            )}
            <Chip label={`地端 ${params.value}`} size="small" variant="outlined" />
          </Stack>
        ),
      },
      {
        field: 'is_enabled',
        headerName: '狀態',
        width: 100,
        type: 'boolean',
        renderCell: (params) => (
          <Chip
            label={params.value ? '啟用' : '停用'}
            color={params.value ? 'success' : 'default'}
            size="small"
          />
        ),
      },
      {
        field: 'actions',
        headerName: '操作',
        width: isAdmin ? 110 : 80,
        sortable: false,
        filterable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="查看儲存空間">
              <IconButton size="small" aria-label="查看儲存空間" onClick={() => setDetailPoolId(params.row.id)}>
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {isAdmin && (
              <IconButton size="small" aria-label="更多操作" onClick={(e) => handleMenuOpen(e, params.row)}>
                <MoreVertIcon fontSize="small" />
              </IconButton>
            )}
          </Stack>
        ),
      },
    ],
    [isAdmin, clouds.length],
  );

  const detailIsSmart = detailPool?.type === 'smart_distribution';
  const detailIsBackup = detailPool?.type === 'backup';
  const detailOnPrem = detailPool?.destinations ?? [];
  const detailTotal = detailOnPrem.length + (detailIsSmart ? clouds.length : 0);

  const memberRows = (rows: DestinationInfo[]) => (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>名稱</TableCell>
          <TableCell>類型</TableCell>
          <TableCell>供應商</TableCell>
          <TableCell>狀態</TableCell>
          <TableCell align="right">優先級</TableCell>
          {detailIsBackup && (
            <TableCell>
              <Tooltip title={COVERAGE_HINT}>
                <span>可單獨還原</span>
              </Tooltip>
            </TableCell>
          )}
          {isAdmin && <TableCell align="right">操作</TableCell>}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((dest) => (
          <TableRow key={dest.id}>
            <TableCell>{dest.name || '-'}</TableCell>
            <TableCell>
              <Chip label={dest.type === 'cloud' ? '雲端' : '地端'} size="small" variant="outlined" />
            </TableCell>
            <TableCell>{dest.provider?.toUpperCase() || '-'}</TableCell>
            <TableCell>{statusChip(dest.status)}</TableCell>
            <TableCell align="right">{dest.priority}</TableCell>
            {detailIsBackup && <TableCell>{coverageCell(dest)}</TableCell>}
            {isAdmin && (
              <TableCell align="right">
                <Tooltip title="移出此池">
                  <IconButton size="small" color="error" aria-label={`移出 ${dest.name}`} onClick={() => setRemovingMember(dest)}>
                    <RemoveCircleOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            儲存池管理
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            管理智慧分散池與備份池及其儲存空間。變更約 1 分鐘內生效，不需重新啟動任何服務。
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} data-testid="storage-pool-add-button">
              新增儲存池
            </Button>
          )}
          <RefreshButton onClick={refresh} loading={loading} />
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {cloudsError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setCloudsError(false)}>
          無法載入雲端目的地清單，智慧分散池的預設成員暫時無法顯示
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <DataGrid
          rows={pools}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 20]}
          disableRowSelectionOnClick
          autoHeight
          localeText={{ noRowsLabel: isAdmin ? '尚未建立儲存池，按「新增儲存池」開始' : '尚未建立儲存池' }}
          slotProps={{
            pagination: {
              labelRowsPerPage: '每頁筆數：',
              labelDisplayedRows: ({ from, to, count }: { from: number; to: number; count: number }) =>
                `${from}-${to} / 共 ${count}`,
            },
          }}
          sx={{ border: 'none', '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }, '& .MuiDataGrid-cell[data-field="actions"]': { justifyContent: 'flex-end' } }}
        />
      </Paper>

      {/* Row menu (admin) */}
      <Menu anchorEl={menuAnchorEl} open={Boolean(menuAnchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => menuPool && openEdit(menuPool)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>編輯</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuPool && toggleEnabled(menuPool)}>
          <ListItemIcon>
            {menuPool?.is_enabled ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </ListItemIcon>
          <ListItemText>{menuPool?.is_enabled ? '停用' : '啟用'}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => menuPool && openDelete(menuPool)} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>刪除</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create / edit dialog (admin) */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth>
        <DialogTitle>{editingPool ? '編輯儲存池' : '新增儲存池'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <TextField
              autoFocus
              label="儲存池名稱"
              fullWidth
              required
              value={form.name}
              onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              disabled={formLoading}
              inputProps={{ maxLength: NAME_MAX, 'data-testid': 'storage-pool-name-input' }}
              {...formFields.bind('name', `最多 ${NAME_MAX} 個字元，不可與其他儲存池重複`)}
            />
            <FormControl fullWidth required disabled={formLoading || Boolean(editingPool)} error={Boolean(formFields.errors.type)}>
              <InputLabel id="storage-pool-type-label">池類型</InputLabel>
              <Select
                labelId="storage-pool-type-label"
                label="池類型"
                name="type"
                value={form.type}
                onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value as StoragePoolType }))}
                data-testid="storage-pool-type-select"
              >
                {STORAGE_POOL_TYPES.map((t) => (
                  <MenuItem key={t} value={t}>
                    {POOL_TYPE_LABELS[t]}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {formFields.errors.type ?? (editingPool ? '池類型建立後不可更改；要換類型請刪除後重新建立' : POOL_TYPE_HINTS[form.type])}
              </FormHelperText>
            </FormControl>
            <TextField
              label="說明"
              fullWidth
              multiline
              rows={3}
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              disabled={formLoading}
              {...formFields.bind('description')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeForm} disabled={formLoading}>
            取消
          </Button>
          <Button onClick={submitForm} variant="contained" disabled={formLoading}>
            {formLoading ? <CircularProgress size={20} /> : editingPool ? '儲存' : '建立'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation (admin) */}
      <Dialog open={deletingPool !== null} onClose={() => !deleteLoading && setDeletingPool(null)}>
        <DialogTitle>確認刪除儲存池</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要刪除「{deletingPool?.name}」嗎？池內的 {deletingPool?.destinations?.length ?? 0} 個地端儲存空間會被移出此池，儲存空間本身與已備份的資料不會被刪除。此操作無法復原。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletingPool(null)} disabled={deleteLoading}>取消</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleteLoading}>
            {deleteLoading ? <CircularProgress size={20} /> : '刪除'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pool detail: members */}
      <Dialog open={detailPool !== null} onClose={() => setDetailPoolId(null)} maxWidth="md" fullWidth>
        {detailPool && (
          <>
            <DialogTitle>
              <Stack direction="row" spacing={1} alignItems="center">
                {POOL_TYPE_ICONS[detailPool.type]}
                <Typography variant="h6">
                  {typeLabel(detailPool.type)} — {detailPool.name}
                </Typography>
                <Chip
                  label={detailPool.is_enabled ? '啟用' : '停用'}
                  color={detailPool.is_enabled ? 'success' : 'default'}
                  size="small"
                />
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              {detailPool.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {detailPool.description}
                </Typography>
              )}

              {detailIsSmart ? (
                <>
                  <Alert severity={clouds.length > 0 ? 'info' : 'warning'} sx={{ mb: 2 }} data-testid="smart-pool-note">
                    {clouds.length > 0 ? SMART_POOL_NOTE : SMART_POOL_NO_CLOUD}
                  </Alert>
                  {clouds.length > 0 && (
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        預設成員：雲端目的地 ({clouds.length})
                      </Typography>
                      <Table size="small" sx={{ mb: 2 }} data-testid="smart-pool-cloud-members">
                        <TableHead>
                          <TableRow>
                            <TableCell>名稱</TableCell>
                            <TableCell>類型</TableCell>
                            <TableCell>供應商</TableCell>
                            <TableCell>狀態</TableCell>
                            <TableCell align="right">優先級</TableCell>
                            {isAdmin && <TableCell align="right">操作</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {clouds.map((c) => (
                            <TableRow key={c.id}>
                              <TableCell>{c.display_name}</TableCell>
                              <TableCell>
                                <Chip label="雲端" size="small" variant="outlined" />
                              </TableCell>
                              <TableCell>{c.cloud_provider?.toUpperCase() || '-'}</TableCell>
                              <TableCell>{statusChip(c.status)}</TableCell>
                              <TableCell align="right">—</TableCell>
                              {isAdmin && (
                                <TableCell align="right">
                                  <Tooltip title="由部署設定，預設納入，不可移除">
                                    <Chip label="預設" size="small" color="primary" variant="outlined" />
                                  </Tooltip>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </>
              ) : (
                <Alert severity="info" sx={{ mb: 2 }} data-testid="backup-pool-note">
                  {BACKUP_POOL_NOTE}
                </Alert>
              )}

              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2">
                  {detailIsSmart ? `地端成員 (${detailOnPrem.length})` : `池內儲存空間 (${detailOnPrem.length})`}
                  {detailIsSmart && clouds.length > 0 && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      合計 {detailTotal} 個儲存空間
                    </Typography>
                  )}
                </Typography>
                {isAdmin && (
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={openAddMember} data-testid="pool-member-add-button">
                    加入儲存空間
                  </Button>
                )}
              </Stack>

              {detailOnPrem.length > 0 ? (
                memberRows(detailOnPrem)
              ) : (
                <Alert severity="info">
                  {detailIsSmart ? '此池尚未加入地端目的地' : '此池尚未加入儲存空間'}
                  {isAdmin ? '，按「加入儲存空間」選擇地端目的地' : ''}
                </Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailPoolId(null)}>關閉</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Add member dialog (admin) */}
      <Dialog open={memberOpen} onClose={closeAddMember} maxWidth="sm" fullWidth>
        <DialogTitle>加入儲存空間</DialogTitle>
        <DialogContent>
          {memberError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {memberError}
            </Alert>
          )}
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            <FormControl fullWidth required disabled={memberLoading || candidatesLoading} error={Boolean(memberFields.errors.destination_id)}>
              <InputLabel id="pool-member-label">儲存空間</InputLabel>
              <Select
                labelId="pool-member-label"
                label="儲存空間"
                name="destination_id"
                value={memberForm.destination_id}
                onChange={(e) => {
                  memberFields.clearField('destination_id');
                  setMemberForm((prev) => ({ ...prev, destination_id: e.target.value }));
                }}
                data-testid="pool-member-select"
              >
                {availableCandidates.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.display_name}（{d.protocol?.toUpperCase() ?? '地端'} · {d.host}）{d.is_enabled ? '' : '（已停用）'}
                  </MenuItem>
                ))}
                {!candidatesLoading && availableCandidates.length === 0 && (
                  <MenuItem value="" disabled>
                    沒有可加入的地端目的地
                  </MenuItem>
                )}
              </Select>
              <FormHelperText>
                {memberFields.errors.destination_id
                  ?? (candidatesLoading ? '載入中…' : PICKER_HELPER[detailPool?.type ?? 'smart_distribution'])}
              </FormHelperText>
            </FormControl>
            <TextField
              label="優先級"
              type="number"
              fullWidth
              value={memberForm.priority}
              onChange={(e) => setMemberForm((prev) => ({ ...prev, priority: e.target.value }))}
              disabled={memberLoading}
              inputProps={{ min: 0, step: 1, 'data-testid': 'pool-member-priority-input' }}
              {...memberFields.bind('priority', '數字越大越優先，預設 0')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeAddMember} disabled={memberLoading}>
            取消
          </Button>
          <Button onClick={submitAddMember} variant="contained" disabled={memberLoading || candidatesLoading}>
            {memberLoading ? <CircularProgress size={20} /> : '加入'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove member confirmation (admin) */}
      <Dialog open={removingMember !== null} onClose={() => !removeLoading && setRemovingMember(null)}>
        <DialogTitle>確認移出儲存空間</DialogTitle>
        <DialogContent>
          <DialogContentText>
            確定要將「{removingMember?.name}」移出「{detailPool?.name}」嗎？儲存空間本身與已備份的資料不會被刪除。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemovingMember(null)} disabled={removeLoading}>取消</Button>
          <Button onClick={confirmRemoveMember} color="error" variant="contained" disabled={removeLoading}>
            {removeLoading ? <CircularProgress size={20} /> : '移出'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar open={!!actionError} autoHideDuration={6000} onClose={() => setActionError(null)}>
        <Alert severity="error" onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      </Snackbar>
      <Snackbar open={!!successMessage} autoHideDuration={3000} onClose={() => setSuccessMessage(null)}>
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
